import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, NETWORKS, fmtUsd, fmtDate, shortHash } from "@/lib/journal";
import { MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";

const DEPTHS = [
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "180", label: "Last 180 days" },
  { id: "365", label: "Last 365 days" },
];

export const WalletImportDialog = ({ open, onOpenChange, portfolios, defaultPortfolioId, onImported }) => {
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("ethereum");
  const [days, setDays] = useState("90");
  const [portfolioId, setPortfolioId] = useState(defaultPortfolioId);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [showSpam, setShowSpam] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => { setPortfolioId(defaultPortfolioId); }, [defaultPortfolioId]);

  const scan = async () => {
    if (!address.trim()) return toast.error("Paste a wallet address first");
    setScanning(true);
    setCandidates(null);
    try {
      const { data } = await api.post("/wallet/scan", {
        address: address.trim(), network, days: parseInt(days, 10),
      }, { params: portfolioId && portfolioId !== "all" ? { portfolio_id: portfolioId } : {} });
      setCandidates(data.candidates);
      setSelected(
        Object.fromEntries(data.candidates.filter((c) => !c.already_imported && !c.spam).map((c) => [c.tx_hash, true]))
      );
      toast.success(
        data.candidates.length
          ? `${data.candidates.length - data.spam_count} real swaps found${data.spam_count ? `, ${data.spam_count} spam/dust hidden` : ""}`
          : "No swaps found in this period"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wallet scan failed");
    } finally {
      setScanning(false);
    }
  };

  const chosen = (candidates || []).filter((c) => selected[c.tx_hash]);
  const spamCount = (candidates || []).filter((c) => c.spam).length;
  const visible = (candidates || []).filter((c) => showSpam || !c.spam);

  const doImport = async () => {
    if (!chosen.length) return toast.error("Select at least one swap");
    setImporting(true);
    try {
      const { data } = await api.post("/trades/import", {
        portfolio_id: portfolioId === "all" ? null : portfolioId,
        address: address.trim(),
        trades: chosen.map((c) => ({
          tx_hash: c.tx_hash, network: c.network, asset: c.asset,
          side: c.side || "buy",
          exit_price: c.exit_price ?? null,
          token_address: c.token_address || null,
          verified: c.verified ?? null,
          volume: c.volume, entry_price: c.entry_price, date: c.date,
        })),
      });
      const sells = data.sells_applied
        ? `, ${data.sells_applied} sell${data.sells_applied === 1 ? "" : "s"} matched to open positions`
        : "";
      toast.success(
        `Imported ${data.imported} buys${sells}${data.skipped ? `, skipped ${data.skipped} duplicates` : ""}`
      );
      onOpenChange(false);
      setCandidates(null);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wallet-import-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Wallet Import <span className="text-[#C58361]">/ Scan</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Paste an address to pull every swap it made, then pick which ones land in the journal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Network</Label>
              <Select value={network} onValueChange={setNetwork}>
                <SelectTrigger data-testid="scan-network-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {NETWORKS.map((n) => (
                    <SelectItem key={n.id} value={n.id} data-testid={`scan-network-${n.id}`}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Depth</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid="scan-depth-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {DEPTHS.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`scan-depth-${d.id}`}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Into Portfolio</Label>
              <Select value={portfolioId} onValueChange={setPortfolioId}>
                <SelectTrigger data-testid="scan-portfolio-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`scan-portfolio-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Wallet Address</Label>
            <div className="flex gap-2">
              <Input
                data-testid="scan-address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={network === "solana" ? "Base58 address…" : "0x…"}
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
              />
              <Button
                data-testid="scan-wallet-btn"
                onClick={scan}
                disabled={scanning}
                className="shrink-0 rounded-none bg-[#C58361] font-medium text-black hover:bg-[#A86B4C]"
              >
                {scanning ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                <span className="ml-2">Scan</span>
              </Button>
            </div>
          </div>

          {candidates && (
            <div className="border border-[#1F1F1F]" data-testid="scan-results">
              {candidates.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-zinc-500">
                  No swaps detected for this address in the selected window.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between border-b border-[#1F1F1F] px-4 py-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {visible.length} swaps · {chosen.length} selected
                    </span>
                    <div className="flex items-center gap-4">
                      {spamCount > 0 && (
                        <button
                          data-testid="scan-toggle-spam"
                          onClick={() => setShowSpam((s) => !s)}
                          className="text-xs text-zinc-500 hover:text-zinc-200"
                        >
                          {showSpam ? "Hide" : "Show"} {spamCount} spam/dust
                        </button>
                      )}
                      <button
                        data-testid="scan-select-all"
                        onClick={() =>
                          setSelected(
                            chosen.length
                              ? {}
                              : Object.fromEntries(visible.filter((c) => !c.already_imported && !c.spam).map((c) => [c.tx_hash, true]))
                          )
                        }
                        className="text-xs text-[#C58361] hover:underline"
                      >
                        {chosen.length ? "Clear" : "Select all new"}
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[300px] divide-y divide-[#1F1F1F] overflow-y-auto">
                    {visible.map((c) => (
                      <label
                        key={c.tx_hash}
                        data-testid={`scan-candidate-${c.tx_hash}`}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors duration-200 ${
                          c.already_imported ? "opacity-40" : "cursor-pointer hover:bg-[#111111]"
                        }`}
                      >
                        <Checkbox
                          data-testid={`scan-check-${c.tx_hash}`}
                          disabled={c.already_imported}
                          checked={!!selected[c.tx_hash]}
                          onCheckedChange={(v) => setSelected((p) => ({ ...p, [c.tx_hash]: !!v }))}
                          className="rounded-none border-[#1F1F1F] data-[state=checked]:border-[#C58361] data-[state=checked]:bg-[#C58361]"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-sm text-white">
                            <span
                              className={`mr-2 border px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                                c.side === "sell"
                                  ? "border-[#EF4444]/40 text-[#EF4444]"
                                  : "border-[#4ADE80]/40 text-[#4ADE80]"
                              }`}
                            >
                              {c.side === "sell" ? "Sell" : "Buy"}
                            </span>
                            {c.volume} {c.asset}
                            <span className="ml-2 text-xs text-zinc-500">
                              {c.side === "sell" ? "→" : "←"} {c.spent_amount} {c.spent_symbol}
                            </span>
                          </p>
                          <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                            {c.date ? fmtDate(c.date) : null} · {shortHash(c.tx_hash)}
                            {c.already_imported && <span className="ml-2 text-[#C58361]">already imported</span>}
                            {c.spam && <span className="ml-2 text-[#EF4444]">{c.spam_reason}</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-zinc-300">{fmtUsd(c.entry_price, 4)}</p>
                          <p className="font-mono text-[10px] text-zinc-600">{fmtUsd(c.cost_basis)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <Button
            data-testid="scan-import-btn"
            onClick={doImport}
            disabled={importing || !chosen.length}
            className="w-full rounded-none bg-[#C58361] py-6 font-medium text-black hover:bg-[#A86B4C] disabled:opacity-40"
          >
            {importing ? "Importing…" : `Import ${chosen.length || ""} Selected`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
